// SPDX-License-Identifier: GPL-3.0-or-later
// HTTP client of the featured-turn routes (contracts.ts ROUTES).
// Free play never calls it. A failure here never blocks the game: every call returns a result,
// never throws. The session is the `pb_session` cookie; the browser sends it on its own.

import {
  type ApiError,
  CLAIM_CODE_PATTERN,
  type ClaimRequest,
  type ClaimResponse,
  type MeResponse,
  type QueueActionResponse,
  type QueueSnapshot,
  ROUTES,
  type TurnStartResponse,
} from "../src/shared/contracts.ts";

/** The result of an API call. `error` is an ApiError code, or "network" or "bad_reply". */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly error: ApiError["error"] | "network" | "bad_reply";
      readonly message: string;
      readonly status: number;
    };

/** The part of `fetch` that the client uses. */
export type FetchLike = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    credentials?: "same-origin";
  },
) => Promise<{ readonly status: number; text(): Promise<string> }>;

/** The featured-turn API. */
export interface FeaturedApi {
  me(): Promise<ApiResult<MeResponse>>;
  queue(): Promise<ApiResult<QueueSnapshot>>;
  claim(code: string, displayName?: string): Promise<ApiResult<ClaimResponse>>;
  join(): Promise<ApiResult<QueueActionResponse>>;
  leave(): Promise<ApiResult<QueueActionResponse>>;
  startTurn(): Promise<ApiResult<TurnStartResponse>>;
  logout(): Promise<ApiResult<{ ok: true }>>;
}

/** Normalize what a person typed into a claim code: trim, upper case, add the "PB-" prefix. */
export function normalizeClaimCode(raw: string): string {
  const compact = raw.trim().toUpperCase().replace(/\s+/g, "");
  const withPrefix = compact.startsWith("PB-") ? compact : `PB-${compact}`;
  // Accept "PB-ABCD1234" as well as "PB-ABCD-1234".
  const bare = /^PB-([0-9A-Z]{4})([0-9A-Z]{4})$/.exec(withPrefix);
  return bare ? `PB-${bare[1]}-${bare[2]}` : withPrefix;
}

/** True when the text has the shape of a claim code (after `normalizeClaimCode`). */
export function isClaimCodeShape(code: string): boolean {
  return CLAIM_CODE_PATTERN.test(code);
}

function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { ok?: unknown }).ok === false &&
    typeof (value as { error?: unknown }).error === "string" &&
    typeof (value as { message?: unknown }).message === "string"
  );
}

type Reply = { readonly status: number; text(): Promise<string> };
type Failure = Extract<ApiResult<unknown>, { ok: false }>;
type RequestInit = NonNullable<Parameters<FetchLike>[1]>;

function failure(error: Failure["error"], message: string, status: number): Failure {
  return { ok: false, error, message, status };
}

function networkFailure(): Failure {
  return failure("network", "The server did not answer.", 0);
}

function badReply(status: number): Failure {
  return failure("bad_reply", "The reply was not valid.", status);
}

/** Send the request. A thrown error (no network) gives null. */
async function send(fetchFn: FetchLike, url: string, init: RequestInit): Promise<Reply | null> {
  try {
    return await fetchFn(url, init);
  } catch {
    return null;
  }
}

/** Read the JSON body of a reply. A body that is not JSON gives `undefined`. */
async function readJson(reply: Reply): Promise<unknown> {
  try {
    return JSON.parse(await reply.text());
  } catch {
    return undefined;
  }
}

function isOkBody(value: unknown): boolean {
  return typeof value === "object" && value !== null && (value as { ok?: unknown }).ok === true;
}

/** GET /api/queue has no `ok` field in its body (QueueSnapshot), so check it by shape. */
function isQueueShape(value: unknown): value is QueueSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const q = value as Partial<QueueSnapshot>;
  return typeof q.length === "number" && Array.isArray(q.entries) && typeof q.paused === "boolean";
}

/** One call of a route that answers with an `ok` body. */
async function callRoute<T extends { ok: true }>(
  fetchFn: FetchLike,
  url: string,
  method: "GET" | "POST",
  body?: unknown,
): Promise<ApiResult<T>> {
  const reply = await send(fetchFn, url, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (reply === null) return networkFailure();
  const parsed = await readJson(reply);
  if (isApiError(parsed)) return failure(parsed.error, parsed.message, reply.status);
  return isOkBody(parsed) ? { ok: true, value: parsed as T } : badReply(reply.status);
}

/** The queue call. It reads the snapshot by shape. */
async function callQueue(fetchFn: FetchLike, url: string): Promise<ApiResult<QueueSnapshot>> {
  const reply = await send(fetchFn, url, { method: "GET", credentials: "same-origin" });
  if (reply === null) return networkFailure();
  const parsed = await readJson(reply);
  if (isApiError(parsed)) return failure(parsed.error, parsed.message, reply.status);
  return isQueueShape(parsed) ? { ok: true, value: parsed } : badReply(reply.status);
}

function claimBody(code: string, displayName: string | undefined): ClaimRequest {
  return displayName === undefined || displayName.trim() === ""
    ? { claim_code: code }
    : { claim_code: code, display_name: displayName };
}

/** Make the client over a `fetch` function. `base` is the origin prefix ("" for same origin). */
export function createFeaturedApi(fetchFn: FetchLike, base = ""): FeaturedApi {
  const url = (path: string): string => `${base}${path}`;
  return {
    me: () => callRoute<MeResponse>(fetchFn, url(ROUTES.me), "GET"),
    queue: () => callQueue(fetchFn, url(ROUTES.queue)),
    claim: (code, displayName) =>
      callRoute<ClaimResponse>(fetchFn, url(ROUTES.claim), "POST", claimBody(code, displayName)),
    join: () => callRoute<QueueActionResponse>(fetchFn, url(ROUTES.queueJoin), "POST"),
    leave: () => callRoute<QueueActionResponse>(fetchFn, url(ROUTES.queueLeave), "POST"),
    startTurn: () => callRoute<TurnStartResponse>(fetchFn, url(ROUTES.turnStart), "POST"),
    logout: () => callRoute<{ ok: true }>(fetchFn, url(ROUTES.logout), "POST"),
  };
}
